/**
 * Screen Time Service
 * Manages screen time limits, tracking, and enforcement.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REBUILT 2026-09-05. Before this date parental screen-time control did not
 * work at all — not partially, not intermittently. Six defects compounded:
 *
 *  1. STORAGE PATH DISAGREEMENT. This service read and wrote settings at
 *     `screenTime/{uid}/settings/current`. The server enforcement
 *     (functions/src/screenTimeGuard.ts) reads `screenTime/{uid}` — the
 *     top-level document. Nothing ever wrote that document, so the guard always
 *     saw "no parental configuration" and failed open. Every limit was ignored.
 *  2. NO RULE FOR THE PATH IT USED. `firestore.rules` matches
 *     `screenTime/{studentUid}` (the document) and
 *     `screenTime/{studentUid}/usage/{date}`. Firestore rules do NOT cascade to
 *     subcollections without `{document=**}`, so `.../settings/current` matched
 *     nothing and was denied by default — both read and write. The rule block's
 *     own comment claimed a "settings" match that was never written.
 *  3. SILENT FAILURE. `getScreenTimeSettings` caught the PERMISSION_DENIED and
 *     returned null, and `checkTimeLimit` reads null as "unrestricted". The
 *     feature failed to the permissive side without a single visible error.
 *  4. DEAD-END UI. `ScreenTimeControls` renders "No screen time settings found"
 *     when the read returns null and offers no way to create them — so with (2)
 *     every parent hit a dead end.
 *  5. THE OVERRIDE WENT NOWHERE. `requestOverride` wrote
 *     `screenTime/{uid}/overrides/{ts}` (also unmatched by any rule, also
 *     denied) and nothing anywhere read that collection. It had no callers.
 *     Removed — the real, PIN-gated path is `updateChildScreenTimeQuota`, which
 *     now writes a bonus this service and the server guard both honour.
 *  6. BREAK REMINDERS RARELY FIRED. `shouldBreak` was
 *     `minutesUsed % interval === 0`. Recorded minutes advance in steps of the
 *     2-minute server heartbeat, so with the configurable 15- or 25-minute
 *     intervals the exact multiple is only hit on even multiples — a reminder
 *     every 30 or 50 minutes instead. Any idle gap skipped it entirely. Now
 *     bucket-based, so crossing a boundary always registers.
 *
 * The canonical settings path is now `screenTime/{studentUid}` — the one the
 * rules secure (linked parent or admin writes; the child may read but not
 * write) and the one the server already enforces against.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  collection,
  doc,
  getDoc,
  query,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { callFunction } from '../utils/functions';

export interface ScreenTimeSettings {
  studentUid: string;
  dailyLimitMinutes: number; // 15-240
  weeklyLimitHours: number; // 1-20
  breakReminderIntervalMinutes: number; // 15, 20, 25, or 30
  allowedDays: string[]; // ['monday', 'tuesday', ...]
  allowedStartTime: string; // 'HH:mm'
  allowedEndTime: string; // 'HH:mm'
  breakScreenMessage: string;
  updatedAt: Date;
}

export interface DailyUsage {
  studentUid: string;
  date: string; // 'YYYY-MM-DD'
  minutesUsed: number;
  sessions: SessionRecord[];
}

export interface SessionRecord {
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
}

export interface TimeCheckResult {
  allowed: boolean;
  remainingMinutes: number;
  shouldBreak: boolean;
  limitReachedAt?: Date;
  nextAllowedTime?: Date;
  /** Reason the session is blocked, when allowed is false */
  blockReason?: 'daily-limit' | 'weekly-limit' | 'outside-days' | 'outside-hours';
  /**
   * Which break interval the student is currently in — 0 before the first is
   * due, 1 after one interval has elapsed, and so on. The caller shows a
   * reminder when this increases, which is what makes the reminder survive a
   * counter that advances in 2-minute steps. See defect (6) in the header.
   */
  breakBucket: number;
  /** Extra minutes granted for today by a PIN-approved parent override. */
  bonusMinutes: number;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * What a parent gets when they open screen-time controls for the first time.
 *
 * Exported because the UI must be able to bootstrap from these — the previous
 * "No screen time settings found" dead end (defect 4) had no path to a first
 * save, which meant no account could ever be configured.
 */
export const DEFAULT_SCREEN_TIME_SETTINGS: Omit<ScreenTimeSettings, 'studentUid' | 'updatedAt'> = {
  dailyLimitMinutes: 120,
  weeklyLimitHours: 10,
  breakReminderIntervalMinutes: 20,
  allowedDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  allowedStartTime: '09:00',
  allowedEndTime: '18:00',
  breakScreenMessage: 'Time for a break! You have been coding for a while.',
};

/**
 * Parse an 'HH:mm' string into minutes-since-midnight. Returns null if invalid.
 */
const parseTimeToMinutes = (value: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value || '');
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/** 'YYYY-MM-DD' in the same form the usage rollup and the server guard use. */
const dateKey = (d: Date): string => d.toISOString().split('T')[0];

export interface UsageHistory {
  date: string;
  minutesUsed: number;
}

/**
 * The single document holding a student's limits, schedule and bonus.
 *
 * Must stay `screenTime/{uid}` — `firestore.rules` secures exactly this path
 * and `functions/src/screenTimeGuard.ts` enforces against exactly this path.
 */
const settingsRef = (studentUid: string) => doc(db.value, 'screenTime', studentUid);

/**
 * Get screen time settings for a student.
 *
 * Returns null when the account has no parental configuration at all — which
 * both this service and the server guard read as "unrestricted". That is the
 * correct default for teachers, admins and adult users, and it is why the
 * silent read failure in defect (3) was invisible: it looked exactly like an
 * unmanaged account.
 */
export const getScreenTimeSettings = async (
  studentUid: string
): Promise<ScreenTimeSettings | null> => {
  try {
    const docSnap = await getDoc(settingsRef(studentUid));

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      studentUid,
      dailyLimitMinutes: data.dailyLimitMinutes || DEFAULT_SCREEN_TIME_SETTINGS.dailyLimitMinutes,
      weeklyLimitHours: data.weeklyLimitHours || DEFAULT_SCREEN_TIME_SETTINGS.weeklyLimitHours,
      breakReminderIntervalMinutes:
        data.breakReminderIntervalMinutes ||
        DEFAULT_SCREEN_TIME_SETTINGS.breakReminderIntervalMinutes,
      allowedDays: data.allowedDays || DEFAULT_SCREEN_TIME_SETTINGS.allowedDays,
      allowedStartTime: data.allowedStartTime || DEFAULT_SCREEN_TIME_SETTINGS.allowedStartTime,
      allowedEndTime: data.allowedEndTime || DEFAULT_SCREEN_TIME_SETTINGS.allowedEndTime,
      breakScreenMessage:
        data.breakScreenMessage || DEFAULT_SCREEN_TIME_SETTINGS.breakScreenMessage,
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  } catch (error) {
    // S101: `null` is what an UNMANAGED account returns, and both this service and
    // the server guard read that as "unrestricted". So a denied or offline read of
    // a MANAGED child's settings resolved to the permissive branch, and
    // `ScreenTimeControls` rendered "no limits set yet" plus the 120-minute
    // platform default as though it were the child's. That is the S13 failure mode
    // exactly: a read failure taking the branch that grants access.
    //
    // The server enforcement (`screenTimeGuard.checkScreenTime`) reads its own
    // document with the Admin SDK, so a limit is still enforced while this throws;
    // what this stops is the PARENT being shown a settings screen that is not
    // theirs, and overwriting real limits by saving from it.
    console.error('Error fetching screen time settings:', error);
    throw error;
  }
};

/** The device's IANA time zone, or UTC when the platform cannot say. */
export const deviceTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** The settings a parent may change — what `updateChildScreenTimeSettings` accepts. */
const SETTINGS_FIELDS = [
  'dailyLimitMinutes',
  'weeklyLimitHours',
  'breakReminderIntervalMinutes',
  'allowedDays',
  'allowedStartTime',
  'allowedEndTime',
  'breakScreenMessage',
] as const;

/**
 * Update screen time settings — with the parent's PIN (S236).
 *
 * The settings were written straight from the parent's browser, so a child on the parent's open
 * session could raise their own limit; only the one-day bonus was behind the PIN. They now go to
 * the `updateChildScreenTimeSettings` Cloud Function, which checks the parent link and the PIN,
 * validates every field, and is the only writer the rules allow.
 *
 * Throws on failure rather than swallowing — a parent who believes they set a limit that was
 * refused is worse off than one who sees the error. The bonus fields are never sent.
 */
export const updateScreenTimeSettings = async (
  _parentUid: string,
  studentUid: string,
  settings: Partial<ScreenTimeSettings>,
  pin: string
): Promise<void> => {
  const changes: Record<string, unknown> = {};
  for (const key of SETTINGS_FIELDS) {
    if (settings[key] !== undefined) changes[key] = settings[key];
  }
  // S231: the allowed days and hours mean the PARENT's clock; the server evaluates them in it.
  changes.timeZone = deviceTimeZone();
  await callFunction('updateChildScreenTimeSettings', { childUid: studentUid, pin, settings: changes });
};

/**
 * S236: check the parent PIN without changing anything — the settings screen asks this when the
 * parent unlocks it, so a mistyped PIN is caught then rather than at the first change.
 */
export const verifyScreenTimePin = async (studentUid: string, pin: string): Promise<void> => {
  await callFunction('updateChildScreenTimeSettings', { childUid: studentUid, pin, settings: {} });
};

/** Why a PIN-gated call was refused, from the server's own words (callFunction keeps them). */
export const pinRefusal = (error: unknown): 'wrong-pin' | 'locked' | 'no-pin' | null => {
  const e = error as { serverMessage?: unknown; message?: unknown } | null;
  const text = `${typeof e?.serverMessage === 'string' ? e.serverMessage : ''} ${
    typeof e?.message === 'string' ? e.message : ''
  }`;
  if (/too many incorrect pins/i.test(text)) return 'locked';
  if (/incorrect pin/i.test(text)) return 'wrong-pin';
  if (/no pin set/i.test(text)) return 'no-pin';
  return null;
};

/**
 * NOTE (2026-09-05): `recordSessionTime` was REMOVED.
 *
 * It wrote client-measured minutes to `screenTime/{uid}/usage/{date}`. That is
 * the document parental screen-time limits are evaluated against, so a client
 * that could write it could report zero minutes and never hit a limit — the
 * control would be advisory only.
 *
 * That path is now written exclusively by the `recordSessionHeartbeat` Cloud
 * Function (functions/src/sessionFunctions.ts), which measures elapsed time
 * with the server clock, and `firestore.rules` denies all client writes to it.
 * Calling the old function would have failed with PERMISSION_DENIED.
 *
 * `requestOverride` was removed the same day — see defect (5) in the header.
 *
 * Read paths below are unchanged — the owner and a linked parent may read.
 */

/**
 * Get usage history for a student, most recent `days` DAYS (not the most
 * recent `days` documents).
 *
 * The distinction is load-bearing: a child who used the app on five days last
 * month would otherwise have those five days counted toward this week's total,
 * because the query returned the last N documents regardless of age. The server
 * guard has always windowed by date; this now matches it.
 */
export const getUsageHistory = async (
  studentUid: string,
  days: number = 7
): Promise<UsageHistory[]> => {
  try {
    const earliest = new Date();
    earliest.setDate(earliest.getDate() - (days - 1));
    const earliestKey = dateKey(earliest);

    // The `where('studentUid','==',studentUid)` that used to sit here was redundant —
    // every document under `screenTime/{studentUid}/usage` already belongs to that
    // student — but combining it with `orderBy('date')` required a composite index on
    // the `usage` collection group which firestore.indexes.json never declared. So the
    // query threw FAILED_PRECONDITION and the catch below rendered an empty history,
    // making a broken query look like a child who had not used the app (S40).
    const constraints = [orderBy('date', 'desc'), limit(days)];

    const q = query(collection(db.value, 'screenTime', studentUid, 'usage'), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((d) => ({
        date: d.data().date as string,
        minutesUsed: (d.data().minutesUsed as number) || 0,
      }))
      .filter((entry) => typeof entry.date === 'string' && entry.date >= earliestKey)
      .reverse(); // Return in ascending date order
  } catch (error) {
    // S101: `[]` renders as "No usage data yet" — a claim about a child's week that
    // a parent acts on. A denied read and the missing composite index of S40 both
    // arrived here as an empty array, so "my child did not go online" and "the
    // query that counts their time is broken" were the same chart.
    console.error('Error fetching usage history:', error);
    throw error;
  }
};

/**
 * Extra minutes a parent has granted for TODAY via the PIN-gated override.
 *
 * Scoped to a single date on purpose — a bonus that outlived the day it was
 * granted for would quietly become a permanent raise to the limit.
 */
const bonusMinutesForToday = (
  data: { bonusMinutes?: unknown; bonusDate?: unknown } | undefined,
  today: string
): number => {
  if (!data || data.bonusDate !== today) return 0;
  const minutes = Number(data.bonusMinutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
};

/**
 * Check if student is within time limit and should take a break.
 *
 * Kept deliberately in step with `checkScreenTime` in
 * functions/src/screenTimeGuard.ts — that one is the enforcement, this one is
 * what the child is shown. They read the same document and apply the same
 * rules so the child never sees a verdict the server disagrees with. If you
 * change one, change both.
 */
export const checkTimeLimit = async (studentUid: string): Promise<TimeCheckResult> => {
  const unrestricted = (): TimeCheckResult => ({
    allowed: true,
    remainingMinutes: DEFAULT_SCREEN_TIME_SETTINGS.dailyLimitMinutes,
    shouldBreak: false,
    breakBucket: 0,
    bonusMinutes: 0,
  });

  try {
    const settingsSnap = await getDoc(settingsRef(studentUid));
    if (!settingsSnap.exists()) {
      // No parental configuration => not a screen-time-managed account.
      return unrestricted();
    }

    const settings = await getScreenTimeSettings(studentUid);
    if (!settings) {
      return unrestricted();
    }

    const now = new Date();
    const today = dateKey(now);
    const bonusMinutes = bonusMinutesForToday(
      settingsSnap.data() as { bonusMinutes?: unknown; bonusDate?: unknown },
      today
    );

    const usageDocRef = doc(db.value, 'screenTime', studentUid, 'usage', today);
    const usageDoc = await getDoc(usageDocRef);

    const minutesUsedToday = usageDoc.exists() ? usageDoc.data().minutesUsed || 0 : 0;
    const dailyRemaining = settings.dailyLimitMinutes + bonusMinutes - minutesUsedToday;

    // Weekly limit: sum the last 7 days of usage and compare against the cap.
    const weeklyHistory = await getUsageHistory(studentUid, 7);
    const minutesUsedThisWeek = weeklyHistory.reduce((sum, day) => sum + day.minutesUsed, 0);
    const weeklyLimitMinutes = settings.weeklyLimitHours * 60 + bonusMinutes;
    const weeklyRemaining = weeklyLimitMinutes - minutesUsedThisWeek;

    // The effective remaining time is the tighter of daily / weekly budgets.
    const remainingMinutes = Math.max(0, Math.min(dailyRemaining, weeklyRemaining));

    // Bucket rather than an exact modulo — see defect (6) in the header.
    const interval = settings.breakReminderIntervalMinutes;
    const breakBucket = interval > 0 ? Math.floor(minutesUsedToday / interval) : 0;
    const shouldBreak = breakBucket > 0;

    // Schedule enforcement — allowed days of week.
    const currentDayName = DAY_NAMES[now.getDay()];
    if (settings.allowedDays.length > 0 && !settings.allowedDays.includes(currentDayName)) {
      return {
        allowed: false,
        remainingMinutes: 0,
        shouldBreak: false,
        breakBucket,
        bonusMinutes,
        blockReason: 'outside-days',
      };
    }

    // Schedule enforcement — allowed time window for the day.
    const startMinutes = parseTimeToMinutes(settings.allowedStartTime);
    const endMinutes = parseTimeToMinutes(settings.allowedEndTime);
    if (startMinutes !== null && endMinutes !== null) {
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      // Treat start === end as "no restriction"; otherwise enforce the window.
      const withinWindow =
        startMinutes === endMinutes ||
        (startMinutes < endMinutes
          ? nowMinutes >= startMinutes && nowMinutes < endMinutes
          : // Overnight window (e.g. 20:00 -> 06:00)
            nowMinutes >= startMinutes || nowMinutes < endMinutes);

      if (!withinWindow) {
        const nextAllowedTime = new Date(now);
        nextAllowedTime.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);
        if (nextAllowedTime <= now) {
          nextAllowedTime.setDate(nextAllowedTime.getDate() + 1);
        }
        return {
          allowed: false,
          remainingMinutes: 0,
          shouldBreak: false,
          breakBucket,
          bonusMinutes,
          nextAllowedTime,
          blockReason: 'outside-hours',
        };
      }
    }

    if (weeklyRemaining <= 0) {
      return {
        allowed: false,
        remainingMinutes: 0,
        shouldBreak,
        breakBucket,
        bonusMinutes,
        blockReason: 'weekly-limit',
      };
    }

    if (dailyRemaining <= 0) {
      return {
        allowed: false,
        remainingMinutes: 0,
        shouldBreak,
        breakBucket,
        bonusMinutes,
        blockReason: 'daily-limit',
      };
    }

    return {
      allowed: remainingMinutes > 0,
      remainingMinutes,
      shouldBreak,
      breakBucket,
      bonusMinutes,
    };
  } catch (error) {
    // Fail OPEN on a read error, matching the server guard: a transient
    // Firestore failure must not lock a child out mid-lesson. The threat model
    // is a child exceeding a configured limit, not infrastructure noise.
    console.error('Error checking time limit:', error);
    return unrestricted();
  }
};
